/** pdcad. The sign-in gate, and then the studio owns the screen.
 *
 * ⚠⚠ **THE GATE IS HERE RATHER THAN INSIDE THE STUDIO ON PURPOSE.** `Studio` fetches the
 * drawing list in its first effect; mounting it before we know who is asking means a burst of
 * 403s in the console on every cold load, and a half-built studio behind a modal — which is the
 * shape of UI that leaks a file name in a page title to someone who never logged in.
 *
 * ⚠ `whoAmI()` is also what puts the CSRF cookie in place (`auth.Me` is `ensure_csrf_cookie`),
 * so it has to run before any POST regardless of whether anyone is signed in.
 */
import { useCallback, useEffect, useState } from "react";

import Login from "./studio/Login";
import Studio from "./studio/Studio";
import { signOut, whoAmI, type Who } from "./studio/api";
import { OFFLINE_WHO, standalone } from "./studio/standalone";

export default function App() {
  const [who, setWho] = useState<Who | null>(null);
  const [asked, setAsked] = useState(false);

  // ⚠⚠ **NO SERVER MEANS NO GATE.** The desktop app has nowhere to sign in to, and phase 3 made
  // the drawing itself server-free — so `standalone()` starts the studio directly. It is asked
  // for, never inferred from a failed request: a web app whose server is down must SAY so rather
  // than quietly become a different product. See `studio/standalone.ts`.
  useEffect(() => {
    if (standalone()) { setWho(OFFLINE_WHO); setAsked(true); return; }
    void whoAmI().then(setWho).finally(() => setAsked(true));
  }, []);

  /** ⚠ Called both by Exit and by any request that came back 401 — a session can expire while
   *  the app is open, and the drafter must land on the login screen rather than on a drawing
   *  that has quietly stopped saving. */
  const out = useCallback(async () => {
    // ⚠ There is nothing to sign out OF when there is no server, and dropping `who` would show a
    // login screen the drafter could never pass.
    if (standalone()) return;
    try { await signOut(); } finally { setWho(null); }
  }, []);

  // ⚠ Nothing until the answer is in. A flash of the login screen for someone who IS signed in
  // is worse than a blank moment: it invites them to type a password they did not need to.
  if (!asked) return <div style={{ position: "absolute", inset: 0, background: "#2b2b2b" }} />;
  if (!who) return <Login onIn={setWho} />;
  return <Studio who={who} onSignOut={out} />;
}
